import UserAndRoleManagementPanel from './UserAndRoleManagementPanel';
import { useTranslation } from "react-i18next";
import BillingPanel from "./BillingPanel";
import InvoicingPanel from "./InvoicingPanel";
import { useEffect, useMemo } from "react";
import { useLocation, useNavigate, useOutletContext } from "react-router-dom";
import GatewayPanel from "./GatewayPanel";
import ContentPanel from "../components/ContentPanel";
import MonitoringPanel from "./MonitoringPanel";
import MailPanel from "./MailPanel";
import JobPanel from "../settings/JobPanel";
import AdministrationMarketplaceStatisticsPanel from "./AdministrationMarketplaceStatisticsPanel";
import { useAtom } from "jotai";
import { authenticationAtom } from "../State";
import { useRoleAccess } from "../hooks/useRoleAccess";
import { configuration } from "../Configuration";
import OperatorResponsibilityPanel from "./operator-responsibility/OperatorResponsibilityPanel";
import DIN276TreePanel from "./din276/DIN276TreePanel";
import ProductImagesPanel from "../settings/ProductImagesPanel";
import DocumentAnalysisPanel from './document-analysis/DocumentAnalysisPanel';
import UserBehaviorPanel from "./UserBehaviorPanel";
import LlmForensicsPanel from "./LlmForensicsPanel";
import GraphExplorerPanel from "./GraphExplorerPanel";
import SynonymsPanel from "./SynonymsPanel";
import DemoTopicsPanel from "./DemoTopicsPanel";
import LeadsCockpitPanel from "./LeadsCockpitPanel";
import { ItemType } from "../components/SideBar";
import type { AppShellContext } from "../components/AppShell";

/**
 * Panel-Inhalt der Administration. Sidebar, Kopfzeile und die Bereichsliste
 * liegen im AppShell (Layout-Route, useSettingsNavigation liefert auf
 * /administration die Admin-Bereiche); diese Seite rendert nur noch das
 * aktive Panel und meldet seinen Titel über den Outlet-Context.
 */
export default function AdministrationPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [authentication] = useAtom(authenticationAtom) as any;
    const { isRootAdmin } = useRoleAccess();
    const { search } = useLocation();
    const searchParams = useMemo(() => new URLSearchParams(search), [search]);
    const selectedPanel = searchParams.get("panel") || "customerManagement";
    const { setTitle, items } = useOutletContext<AppShellContext>();

    useEffect(() => {
        if (!configuration.shouldSeeAdministration(authentication)) {
            navigate('/');
        }
    }, [authentication, navigate]);

    useEffect(() => {
        const activeItem = items.find(
            (item) => item.type === ItemType.item && item.key === selectedPanel,
        );
        setTitle(String(activeItem?.text || t("administrationPage.administration", { defaultValue: "Administration" })));
    }, [items, selectedPanel, setTitle, t]);

    return (
        <ContentPanel backgroundColor="transparent">
            {(() => {
                switch (selectedPanel) {
                    // Kunden & Nutzer, darunter für den Plattform-Root die Plattformrollen. „teamManagement“
                    // war derselbe Inhalt unter zweitem Namen; alte Links landen hier.
                    case 'customerManagement':
                    case 'teamManagement': return <UserAndRoleManagementPanel showPlatformRoles={isRootAdmin} />;
                    case 'platformRoles': return <UserAndRoleManagementPanel showPlatformRoles={isRootAdmin} initialTab="platformRoles" />;
                    case 'email': return <MailPanel />;
                    case 'billing': return <BillingPanel></BillingPanel>;
                    case 'invoicing': return <InvoicingPanel></InvoicingPanel>
                    case 'telemetry': return <GatewayPanel></GatewayPanel>;
                    case 'jobs': return <JobPanel />;
                    case 'monitoring': return <MonitoringPanel />;
                    case 'statistics': return <AdministrationMarketplaceStatisticsPanel />;
                    case 'operatorResponsibility': return <OperatorResponsibilityPanel />;
                    case 'documentAnalysis': return isRootAdmin ? <DocumentAnalysisPanel /> : null;
                    case 'din276Tree': return isRootAdmin ? <DIN276TreePanel /> : null;
                    case 'productImages': return isRootAdmin ? <ProductImagesPanel /> : null;
                    case 'userBehavior': return isRootAdmin ? <UserBehaviorPanel /> : null;
                    case 'llmForensics': return isRootAdmin ? <LlmForensicsPanel /> : null;
                    case 'synonyms': return isRootAdmin ? <SynonymsPanel /> : null;
                    case 'demoTopics': return isRootAdmin ? <DemoTopicsPanel /> : null;
                    case 'leadsCockpit': return isRootAdmin ? <LeadsCockpitPanel /> : null;
                    case 'graphExplorer': return isRootAdmin ? <GraphExplorerPanel /> : null;
                }
            })()}
        </ContentPanel>
    );
}
